import { Config } from '@/services/ConfigService'
import { Axios } from '@/plugins/axios'

import qs from 'qs'

import Interceptor from '@/plugins/axios/DefaultInterceptor'
import UploaderManager from '@/plugins/axios/UploadManagerSingleton'
import { reportRequestTimeout } from '@/plugins/axios/RequestTimeoutMonitor'

const RequestsPriority = {
	low: 'low',
	high: 'high',
	auto: 'auto'
}

const REQUEST_TIMEOUT_MS = 30000

const defaultConfiguration = baseUrl => {
	const axios = Axios.create({
		baseURL: baseUrl,
		withCredentials: false,
		responseType: 'json',
		responseEncoding: 'utf8',
		timeout: REQUEST_TIMEOUT_MS,
		validateStatus: function (status) {
			return status >= 200 && status < 300
		},
		paramsSerializer: function (params) {
			return qs.stringify(params, { arrayFormat: 'brackets' })
		}
	})
	axios.interceptors.request.use(Interceptor.onApiRequestSuccess, Interceptor.onRequestError.bind({ axios: axios }))
	axios.interceptors.response.use(Interceptor.onResponseSuccess, Interceptor.onResponseError.bind({ axios: axios }))
	axios.chunkUpload = (url, file, params) => {
		return UploaderManager.upload(`${baseUrl}${url}`, file, params)
	}
	return axios
}

const handleResponseDataExtraction = response => {
	let result = null
	//TODO: FIX BACKEND to have homogeneous responses
	if (response?.data?.hasOwnProperty('data') && !response?.data?.hasOwnProperty('pagination') && !response?.data?.hasOwnProperty('per_page')) {
		result = response.data.data
	} else if (response?.data) {
		result = response.data
	}
	return result
}

export const Configurator = {
	defaultConfiguration: defaultConfiguration,
	CancelToken: Axios.CancelToken
}

class RequestManager {
	_runningRequests
	_axios
	_baseUrl
	constructor(baseURL) {
		this._runningRequests = []
		this._axios = defaultConfiguration(baseURL)
		this._baseUrl = baseURL
	}
	_cancelPreviousRequest(method, uri, args, parameters) {
		if (method == 'GET') {
			const previousRequest = this._getPreviousRequest(method, uri, args, parameters)
			if (previousRequest) {
				if (previousRequest instanceof AbortController) {
					previousRequest.abort()
				} else {
					previousRequest.cancelTokenSource.cancel(previousRequest.cancelToken)
				}
			}
		}
	}
	_cancelAllOtherRequests(method, uri, args) {
		if (method == 'GET') {
			const url = uri(...args)
			this._runningRequests
				.filter(runningRequest => runningRequest.method == method && runningRequest.url == url)
				.forEach(runningRequest => {
					if (runningRequest instanceof AbortController) {
						runningRequest.abort()
					} else {
						runningRequest.cancelTokenSource.cancel(runningRequest.cancelToken)
					}
				})
		}
	}
	_getPreviousRequest(method, uri, args, parameters = {}) {
		return method !== 'GET' ? undefined : this._runningRequests.find(runningRequest => this._compareRequests(runningRequest, method, uri, args, parameters))
	}
	_compareRequests(referenceRequest, method, uri, args, parameters = {}) {
		if (parameters === null) {
			parameters = {}
		}
		const url = uri(...args)
		const referenceRequestMethod = referenceRequest?.method ?? ''
		const referenceRequestURL = referenceRequest?.url ?? ''
		const referenceRequestParams = referenceRequest?.params ?? {}
		return method == referenceRequestMethod && url == referenceRequestURL && this._requestParametersAreEquals(referenceRequestParams, parameters)
	}
	_requestParametersAreEquals(referenceRequestParameters = {}, parameters = {}) {
		if (referenceRequestParameters === null) {
			referenceRequestParameters = {}
		}
		if (parameters === null) {
			parameters = {}
		}
		const referenceParametersKeys = Object.keys(referenceRequestParameters).filter(key => typeof key === 'string' || key instanceof String)
		const parametersKeys = Object.keys(parameters).filter(key => typeof key === 'string' || key instanceof String)
		let parametersAreTheSame = referenceParametersKeys.length === parametersKeys.length
		for (const key of parametersAreTheSame ? parametersKeys : []) {
			const referenceValue = referenceRequestParameters[key]
			const value = parameters[key]
			const valuesAreObjects = referenceValue !== null && value !== null && typeof referenceValue === 'object' && typeof value === 'object'
			if (
				referenceValue == null ||
				(valuesAreObjects && !this._requestParametersAreEquals(referenceValue, value)) ||
				(!valuesAreObjects && referenceValue != value)
			) {
				parametersAreTheSame = false
				break
			}
		}
		return parametersAreTheSame
	}
	_recordNewRunningRequest(method, uri, args, parameters, data, newCancelSource, response) {
		this._runningRequests.push({
			method: method,
			uri: uri,
			args: args,
			url: uri(...args),
			params: parameters,
			data: data,
			cancelTokenSource: newCancelSource,
			cancelToken: newCancelSource.token,
			response: response
		})
	}
	_clearRunningRequest(method, url, parameters = null) {
		const runningRequestIndex = this._runningRequests.findIndex(
			runningRequest => runningRequest.method == method && runningRequest.url == url && runningRequest.params == parameters
		)
		if (runningRequestIndex != -1) {
			this._runningRequests.splice(runningRequestIndex, 1)
		}
	}
	_runFetchApiRequest(method, url, parameters, data, newCancelSource, importance) {
		let hasTimedOut = false
		const timeoutId = setTimeout(() => {
			hasTimedOut = true
			newCancelSource.abort()
		}, REQUEST_TIMEOUT_MS)
		return fetch(`${Config.VUE_APP_ROOT_API}${url}`, {
			method: method,
			headers: new Headers({
				Accept: 'application/json',
				'Accept-Encoding': 'gzip, deflate, br',
				Authorization: localStorage.getItem('token'),
				Host: Config.VUE_APP_ROOT_API
			}),
			mode: 'cors',
			cache: 'default',
			referrer: window.location,
			keepalive: false,
			priority: importance,
			signal: newCancelSource.signal
		})
			.then(response => response.json())
			.then(response => handleResponseDataExtraction(response))
			.catch(e => {
				if (e.name !== 'AbortError') {
					throw e
				}
				if (hasTimedOut) {
					reportRequestTimeout({ client: 'fetch', method: method, url: url, params: parameters, timeout: REQUEST_TIMEOUT_MS })
				}
			})
			.finally(() => clearTimeout(timeoutId))
	}
	_runRequest(method, url, parameters, data, newCancelToken, chunkUpload = false, showError = true, showErrorInSnackbar = true) {
		const request = {
			url: url,
			method: method.toLowerCase(),
			cancelToken: newCancelToken
		}
		if (parameters) {
			request.params = parameters
		}
		if (data) {
			request.data = data
		}
		if (!showError) {
			request.show_error = showError
		}
		if (!showErrorInSnackbar) {
			request.show_error_in_snackbar = showErrorInSnackbar
		}
		let result = null
		if (chunkUpload) {
			result = this._axios.chunkUpload(url, data, parameters)
		} else {
			result = this._axios.request(request)
		}
		return result
			.catch(error => {
				if (!Axios.isCancel(error)) {
					throw error
				}
			})
			.then(response => handleResponseDataExtraction(response))
	}
	_execRequest(
		useFetchApi,
		method,
		uri,
		args = [],
		params = null,
		data = null,
		doCancelPreviousRequest = false,
		doCancelAllOtherRequests = false,
		chunkUpload = false,
		importance = RequestsPriority.auto,
		showError = true,
		showErrorInSnackbar = true
	) {
		const url = uri(...args)
		const parameters = params
		let result
		if (method == 'GET' && this._getPreviousRequest(method, uri, args, parameters)) {
			result = Promise.resolve(this._getPreviousRequest(method, uri, args, parameters).response)
		} else {
			if (doCancelAllOtherRequests) {
				this._cancelAllOtherRequests(method, uri, args)
			}
			if (doCancelPreviousRequest) {
				this._cancelPreviousRequest(method, uri, args, parameters)
			}
			let newCancelSource = params?.cancelToken
			if (useFetchApi) {
				newCancelSource = new AbortController()
				result = this._runFetchApiRequest(method, url, parameters, data, newCancelSource, importance).finally(() => {
					this._clearRunningRequest(method, url, parameters)
				})
			} else {
				if (!newCancelSource) {
					newCancelSource = Configurator.CancelToken.source()
				}
				result = this._runRequest(method, url, parameters, data, newCancelSource.token, chunkUpload, showError, showErrorInSnackbar).finally(() => {
					this._clearRunningRequest(method, url, parameters)
				})
			}
			this._recordNewRunningRequest(method, uri, args, parameters, data, newCancelSource, result)
		}
		return result
	}
	getRequest(
		uri,
		args,
		params = null,
		doCancelPreviousRequest = false,
		doCancelAllOtherRequests = false,
		importance = RequestsPriority.auto,
		doShowError = true,
		doShowErrorInSnackBar = true
	) {
		return this._execRequest(
			false,
			'GET',
			uri,
			args,
			params,
			null,
			doCancelPreviousRequest,
			doCancelAllOtherRequests,
			false,
			importance,
			doShowError,
			doShowErrorInSnackBar
		)
	}
	postRequest(
		uri,
		args,
		params = null,
		data = null,
		doCancelPreviousRequest = false,
		doCancelAllOtherRequests = false,
		importance = RequestsPriority.auto,
		doShowError = true,
		doShowErrorInSnackBar = true
	) {
		return this._execRequest(
			false,
			'POST',
			uri,
			args,
			params,
			data,
			doCancelPreviousRequest,
			doCancelAllOtherRequests,
			false,
			importance,
			doShowError,
			doShowErrorInSnackBar
		)
	}
	patchRequest(
		uri,
		args,
		params = null,
		data = null,
		doCancelPreviousRequest = false,
		doCancelAllOtherRequests = false,
		importance = RequestsPriority.auto,
		doShowError = true,
		doShowErrorInSnackBar = true
	) {
		return this._execRequest(
			false,
			'PATCH',
			uri,
			args,
			params,
			data,
			doCancelPreviousRequest,
			doCancelAllOtherRequests,
			false,
			importance,
			doShowError,
			doShowErrorInSnackBar
		)
	}
	putRequest(
		uri,
		args,
		params = null,
		data = null,
		doCancelPreviousRequest = false,
		doCancelAllOtherRequests = false,
		importance = RequestsPriority.auto,
		doShowError = true,
		doShowErrorInSnackBar = true
	) {
		return this._execRequest(
			false,
			'PUT',
			uri,
			args,
			params,
			data,
			doCancelPreviousRequest,
			doCancelAllOtherRequests,
			false,
			importance,
			doShowError,
			doShowErrorInSnackBar
		)
	}
	deleteRequest(
		uri,
		args,
		params = null,
		doCancelPreviousRequest = false,
		doCancelAllOtherRequests = false,
		importance = RequestsPriority.auto,
		doShowError = true,
		doShowErrorInSnackBar = true
	) {
		return this._execRequest(
			false,
			'DELETE',
			uri,
			args,
			params,
			null,
			doCancelPreviousRequest,
			doCancelAllOtherRequests,
			false,
			importance,
			doShowError,
			doShowErrorInSnackBar
		)
	}
	chunkUpload(
		uri,
		args,
		params = null,
		file = null,
		doCancelPreviousRequest = false,
		doCancelAllOtherRequests = false,
		importance = RequestsPriority.auto,
		doShowError = true,
		doShowErrorInSnackBar = true
	) {
		return this._execRequest(
			false,
			'POST',
			uri,
			args,
			params,
			file,
			doCancelPreviousRequest,
			doCancelAllOtherRequests,
			true,
			importance,
			doShowError,
			doShowErrorInSnackBar
		)
	}
	fetchApiGetRequest(
		uri,
		args,
		params = null,
		doCancelPreviousRequest = false,
		doCancelAllOtherRequests = false,
		importance = RequestsPriority.auto,
		doShowError = true,
		doShowErrorInSnackBar = true
	) {
		return this._execRequest(
			true,
			'GET',
			uri,
			args,
			params,
			null,
			doCancelPreviousRequest,
			doCancelAllOtherRequests,
			false,
			importance,
			doShowError,
			doShowErrorInSnackBar
		)
	}
}

export const Requester = function (baseURL) {
	const requestManager = new RequestManager(baseURL)
	return {
		GET: function (...args) {
			return requestManager.getRequest(...args)
		},
		POST: function (...args) {
			return requestManager.postRequest(...args)
		},
		PATCH: function (...args) {
			return requestManager.patchRequest(...args)
		},
		PUT: function (...args) {
			return requestManager.putRequest(...args)
		},
		DELETE: function (...args) {
			return requestManager.deleteRequest(...args)
		},
		CHUNKUPLOAD: function (...args) {
			return requestManager.chunkUpload(...args)
		},
		fetchApi: {
			GET: function (...args) {
				return requestManager.fetchApiGetRequest(...args)
			},
			priority: RequestsPriority
		}
	}
}

export const Backend = Requester(Config.VUE_APP_ROOT_API)
